import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import { NextSeo } from 'next-seo'
import { useTranslations } from 'next-intl'
import TRANSLATIONS from '@locales/index'
import { getClient } from '@lib/sanity.server'
import { FooterFooterNavigationQuery } from '@lib/sanity.queries'
import { getQrActivationPartners } from '@lib/utils/qrActivationPartners'

// Client-only — avoids SSR issues with browser APIs used in the component
const QrActivation = dynamic(
  () => import('@components/qrActivation/QrActivation'),
  { ssr: false }
)

export async function getServerSideProps({ locale = 'de' }: GetServerSidePropsContext) {
  const footerNav = await getClient(false).fetch(FooterFooterNavigationQuery, { lang: locale })

  return {
    props: {
      messages: TRANSLATIONS[locale],
      clean: true,
      noFooter: true,
      noHeader: true,
      footerLinks: footerNav?.items ?? [],
      partners: getQrActivationPartners(),
    }
  }
}

/**
 * Page metadata lives HERE, not in the component: `QrActivation` is loaded
 * with `ssr: false`, so anything it rendered into <head> only appeared after
 * hydration — crawlers and link previews got the shop's STI defaults and
 * `index,follow`. The page renders on the server, so this is what they see.
 */
function QrActivationSeo() {
  const t = useTranslations('QrActivation')
  return (
    <NextSeo
      title={t('seoTitle')}
      description={t('seoDescription')}
      openGraph={{ title: t('seoTitle'), description: t('seoDescription') }}
      noindex={true}
      nofollow={true}
    />
  )
}

export default function QrActivationPage({ footerLinks, partners }: { footerLinks: any[]; partners: any[] }) {
  return (
    <>
      <QrActivationSeo />
      <QrActivation footerLinks={footerLinks} partners={partners} />
    </>
  )
}
